import { CertificationGoalContext } from '../../../../certification-goal-context';
import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import cx from "classnames";

import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { CheckCircleOutline as CheckCircleOutlineIcon } from '@vedmarket/ui-kit';
import AccordionSummary from "@mui/material/AccordionSummary";
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import { CheckCircle as CheckCircleIcon } from '@vedmarket/ui-kit';

import {formatHSCode, isCalculationViewModeError} from "../../../../../../../../../utils/helpers";
import {useLazyGetTnvedQuery} from "../../../../../../../../../api/tnved";

import {CERTIFICATION_AUTO_FORM, CertificationFormType} from "../../index";
import {useGetCalcIdFromRoute} from "../../../../../../../../../utils/hooks";

import CertificationCodeCuTr from "./components/cu-tr";

import COLORS from "../../../../../../../../../styles/colors.scss";

import {
  setBrokerDataMutation,
  useLazyGetBrokerDataByCodeQuery,
} from "../../../../../../../../../api/calc";

import './certification-code.scss';
import {LinkPopover} from '@vedmarket/ui-kit';
import './certification-help.scss';
import ModalCode from "../../../../../customs/components/modal_code";
import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../common/notification";
import CustomCertification from "./components/custom-certification";
import {BROKER_ACTIONS} from "../../../../../../../../../utils/constants";
import {useCalculationReadOnly} from '../../../../../../calculation-read-only';

type Props = {
  index: number
  code: Declaration
}

const CertificationCode = ({ index, code }: Props) => {

  const { t } = useTranslation();
  const readOnly = useCalculationReadOnly();
  const { report } = React.useContext(CertificationGoalContext);
  const [ isExpanded, setIsExpanded ] = useState(true);
  const reportVisibleDocument = React.useCallback((fields: Record<string, unknown>) => {
    const visibleFields = Object.fromEntries(Object.entries(fields).map(([ key, value ]) => [ `${index}_${key}`, isExpanded ? value : undefined ]));
    report(visibleFields);
  }, [ report, isExpanded, index ]);

  const orderId = useGetCalcIdFromRoute()

  const [ tnvedRequest ] = useLazyGetTnvedQuery()

  const [ tnvedInfo, setTnvedInfo ] = useState<TnvedInfo>();
  const [ openModalCode, setOpenModalCode ] = useState(false);

  const {  control, getValues, setValue, trigger, watch } = useFormContext<CertificationFormType>()

  const [ hasCertificateValueHasChanged, setHasCertificateValueHasChanged ] = React.useState(false);

  const hasCertificate = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] == 'true' || false;
  const certificateIsNotChosen = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'false';
  const hasCustomCertification = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'custom';

  const [ triggerGetBrokerDataByCode ] = useLazyGetBrokerDataByCodeQuery();

  const [ setBrokerData ] = setBrokerDataMutation()

  useEffect(() => {

    if (!code?.code) return;

    if (!tnvedInfo) {
      tnvedRequest(code.code)?.then((response) => {
        setTnvedInfo(response?.data);
      });
    }

  }, [ code?.code ])

  const hasCert = useWatch({
    control,
    name: `codes.${index}.${CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE}`,
  });

  useEffect(() => {

    const setHasCertificate = async () => {
      if (readOnly) {
        return;
      }

      if (hasCertificateValueHasChanged) {
        let certificateValue: boolean | null | undefined;

        const certificateIsNull = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE] === true ||
            getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'null';

        if (hasCert === "true") {
          certificateValue = true;
        } else if (certificateIsNull) {
          certificateValue = null;
        } else {
          certificateValue = undefined;
        }

        const action = getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.ACTION];

        if (certificateValue !== undefined && (action !== BROKER_ACTIONS.CERT_MANUAL)) {
          const result = await setBrokerData({
            orderId,
            code: code.code,
            has_certificate: certificateValue,
            countryCode: code.country_data?.code,
            action: action,
          });

          if (isCalculationViewModeError(result)) {
            return;
          }

          await triggerGetBrokerDataByCode(orderId);
        }
      }
    };

    setHasCertificate();

  }, [ hasCert, orderId, code?.code ]);

  const handleChangeSetCertificate = (value: string, noNeedCertificate: boolean) => {
    if (readOnly) {
      return;
    }
    // @ts-ignore
    setValue(`codes.${index}.${CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE}`, value);

    let action: string = '';

    if (value === 'true') {
      action = BROKER_ACTIONS.CERT_EXISTS;
    } else if (value === 'false' && noNeedCertificate){
      action = BROKER_ACTIONS.CERT_FIND_MISSING_REGULATION;
    } else if (value === 'custom') {
      action = BROKER_ACTIONS.CERT_MANUAL;
    } else if (value === 'null') {
      action = BROKER_ACTIONS.CERT_SKIP;
    }

    // @ts-ignore
    setValue(`codes.${index}.${CERTIFICATION_AUTO_FORM.ACTION}`, action);

    trigger();
  }

  const documentTypes = getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.DOCUMENT_TYPES];

  useEffect(() => {
    const reportCode = () => {
      const values = getValues()?.codes?.[index];
      report({
        [`${index}_${code.code}`]: isExpanded ? {
          code: code.code,
          has_certificate: values?.has_certificate,
          ...(values?.has_certificate === 'custom' ? { cost_total_manual: values.cost_total_manual } : {}),
        } : undefined,
      });
    };

    reportCode();
    const subscription = watch(reportCode);

    return () => {
      subscription.unsubscribe();
      report({ [`${index}_${code.code}`]: undefined });
    };
  }, [ report, getValues, watch, index, code.code, isExpanded ]);

  const isCompleted = getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'true' ||
      (documentTypes?.length > 0 && documentTypes.every(document => document.has_broker === "true")) ||
      getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'false'
      && getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE] === true ||
      getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'null' ||
      getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] === 'custom' &&
      getValues()?.codes?.[index][CERTIFICATION_AUTO_FORM.COST_TOTAL_MANUAL]
  ;

  const handleOpenCodeModal = (e: any) => {
    e.stopPropagation();
    setOpenModalCode(true);
  }

  const hasDocumentTypes = documentTypes?.length > 0;
  const noNeedCertificate = documentTypes?.length === 0 && hasCert === 'false';

  return <>
    <div className={cx('certification-code', {
      'certification-code_completed': isCompleted,
    })}>
      <Accordion sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        marginBottom: 0,
        '&.Mui-expanded': {
          marginBottom: 0,
        },
      }}
      defaultExpanded onChange={(_, expanded) => setIsExpanded(expanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: '0',
            minHeight: '24px',
            alignItems: 'start',
            '.MuiAccordionSummary-content': {
              margin: 0,
            },
            '.MuiAccordionSummary-expandIconWrapper': {
              width: '24px',
              flexShrink: 0,
              marginLeft: '16px',
              color: COLORS.LIGHT_ACTION,
            },
            '.MuiAccordionSummary-expandIconWrapper svg': {
              width: '24px',
              height: '24px',
            },
            '&.Mui-expanded': {
              borderBottom: 'none',
              '.MuiAccordionSummary-content': {
                margin: 0,
              }
            },
          }}
        >
          <Typography
            component='div'
            variant='subtitle1'
            sx={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: '24px'
            }}
            className='certification-code__title-row'
          >
            <div className='certification-code__title-row-left'>
              {isCompleted ? <CheckCircleIcon style={{
                color: COLORS.GREEN,
                marginRight: '16px',
                width: '24px',
                height: '24px',
                flexShrink: 0,
              }} /> : <CheckCircleOutlineIcon style={{
                color: COLORS.SLIGHTLY_GRAY,
                marginRight: '16px',
                width: '24px',
                height: '24px',
                flexShrink: 0,
              }} />}
              <div className='certification-code__column'>
                <div className='certification-code__code-info'>
                  <Typography component='span' variant='subtitle1' sx={{
                    fontWeight: 500,
                    lineHeight: '24px',
                    color: COLORS.BLACK_DARK,
                  }}>{index + 1}.</Typography>
                  {code?.code && <div className='certification-code__link-container'>
                    <Typography
                      variant='body2'
                      className='certification-code__link'
                      sx={{
                        color: COLORS.BLUE,
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        '&:hover': {
                          color: COLORS.BLUE_DARK,
                        },
                      }}
                      onClick={handleOpenCodeModal}
                    >
                      {formatReferenceCopy(`${t('common__text__codeOfHS')} ${formatHSCode(code?.code)} (${code.country_data?.code})`)}
                    </Typography>
                  </div>}
                </div>
                {tnvedInfo?.GoodInfo?.Name && <Typography
                  variant='body2'
                  sx={{
                    display: 'block',
                    marginTop: '8px',
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: 0,
                    color: COLORS.LIGHT_TEXT_DISABLED
                  }}
                >{formatReferenceCopy(tnvedInfo.GoodInfo.Name)}</Typography>}
              </div>
            </div>
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{
          padding: '24px 0 0',
        }}>
          <Controller
            rules={{
              required: hasCertificate || t('common__text__isRequired'),
            }}
            render={({ field }) => (
              <FormControl fullWidth
                {...field}
                sx={{
                  margin: 0,
                  gap: '8px',
                }}
              >
                <InputLabel id={`certification-${index}-label`} shrink>
                  <Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('calc__text__step5__name'))}
                  </Typography>
                </InputLabel>
                <Select
                  notched
                  disabled={readOnly}
                  onChange={(e) => {
                    setHasCertificateValueHasChanged(true);
                    field.onChange(e);
                    handleChangeSetCertificate(e.target.value, !hasDocumentTypes);
                  }}
                  labelId={`certification-${index}-label`}
                  id='dictionaries__text__certification__thereIsAValidCertificate'
                  value={getValues()?.codes?.[index]?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE]}
                  label={formatReferenceCopy(t('calc__text__step5__name'))}>
                  <MenuItem value='true'><Typography component='span' variant='inherit'>{formatReferenceCopy(t('dictionaries__text__certification__thereIsAValidCertificate'))}</Typography></MenuItem>
                  <MenuItem value='false'><Typography component='span' variant='inherit'>{formatReferenceCopy(t('dictionaries__text__certification__calculateTheCostOfTheCertificate'))}</Typography></MenuItem>
                  <MenuItem value='custom'><Typography component='span' variant='inherit'>{formatReferenceCopy(t('dictionaries__text__certification__specifyTheCostOfCertification'))}</Typography></MenuItem>
                  <MenuItem value='null'><Typography component='span' variant='inherit'>{formatReferenceCopy(`${t('dictionaries__text__certification__skipCertificationFor')} ${t('declension__text__HSCodeSeveral')} ${formatHSCode(code?.code)}`)}</Typography></MenuItem>
                </Select>
                <LinkPopover
                  linkText={t('calc__help__certificationChoiceLink')}
                  text={<div className='certification-help'>
                    <Typography variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationChoiceExisting'))}
                    </Typography>
                    <Typography variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationChoiceEstimate'))}
                    </Typography>
                    <Typography variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationChoiceCustom'))}
                    </Typography>
                    <Typography variant='inherit'>
                      {formatReferenceCopy(t('calc__help__certificationChoiceSkip'))}
                    </Typography>
                  </div>}
                />
              </FormControl>
            )}
            name={`codes.${index}.${CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE}`}
            control={control}
          />
          {certificateIsNotChosen && hasDocumentTypes && (
            <>
              <Typography variant='body2' sx={{
                color: COLORS.BLACK_DARK,
                marginTop: '24px',
                lineHeight: '21px',
              }}>
                {formatReferenceCopy(t('dictionaries__text__certification__chooseFromTheSuggestedOptions'))}
              </Typography>
              <CertificationGoalContext.Provider value={{ fields: {}, report: reportVisibleDocument }}>
                {documentTypes?.map((documentType: DocumentsByCodeItem, documentIndex: number) => <CertificationCodeCuTr
                  key={documentType.id}
                  index={documentIndex}
                  documentType={documentType}
                  countryCode={code?.country_data?.code}
                  code={code?.code}
                />
                )}
              </CertificationGoalContext.Provider>
            </>
          )}
          {hasCustomCertification && <CustomCertification
            index={index}
            code={code.code}
            country={code.country_data.code}
          />}
          {noNeedCertificate &&  <Notification
            type={NOTIFICATION_TYPE.SUCCESS}
            title={`${t('dictionaries__text__certification__for')} ${t('declension__text__HSCodeSeveral')} ${formatHSCode(code?.code)} ${t('dictionaries__text__certification__certificationIsNotRequired')}`}
            sx={{
              marginTop: 3,
            }}
            noCloseButton
          />}
        </AccordionDetails>
      </Accordion>
    </div>
    <ModalCode
      isOpen={openModalCode}
      onClose={() => setOpenModalCode(false)}
      insideModal
      code={code?.code}
      tnvedInfo={tnvedInfo}
    />
  </>
}

export default CertificationCode
